import { useMutation, useQuery, useQueryClient } from "react-query";
import axios from "axios";
import { nuclearn_api_url } from "src/App";
import {
  useNotificationStream,
  isNotificationStreamHealthy,
  useSSEFallbackInvalidation,
} from "src/reusable/hooks/useNotificationStream";

export interface ProposalGenerationRun {
  id: number;
  create_date: string;
  dialogue_ids: number[];
  input_artifact_ids: number[];
  dataset_ids: number[];
  template_id: number | null;
  report_sdr_id: number | null;
}

export interface CreateProposalGenerationRunPayload {
  dialogue_id: number;
  input_artifact_ids: number[];
  dataset_ids?: number[];
  template_id: number | null;
  report_type?: string;
  sequence_number?: number;
}

export interface ProposalGenerationDialogueData {
  proposal_generation_run_id: number | null;
  input_artifact_ids: number[];
  dataset_ids: number[];
  template_id: number | null;
  report_sdr_id: number | null;
  report_type?: string;
  sequence_number?: number;
}

export interface UpdateProposalGenerationDialogueData {
  input_artifact_ids: number[];
  dataset_ids: number[];
  template_id: number | null;
  report_sdr_id: number | null;
  report_type?: string;
  sequence_number?: number;
}

export interface StageResponse {
  sequence_number: number;
  report_type: string;
  // The run's snapshotted stage report_type prefix.
  stage_report_prefix: string | null;
  // The run's snapshotted stage display suffix (e.g. "Discipline Report").
  stage_report_suffix_label: string | null;
  dialogue_id: number;
  status: "pending" | "in_progress" | "completed" | "failed";
  report_sdr_id: number | null;
  template_id: number | null;
  created_at: string;
  completed_at: string | null;
  error_message?: string | null;
}

export interface CreateStagesPayload {
  stages: string[];
  review_report_sdr_id: number;
}

// Moved to the shared workflow shell; re-exported so PG-internal imports and
// query-key call sites stay unchanged.
export {
  fetchEnrichedArtifacts,
  type Artifact,
  type Dataset,
} from "src/views/products/workflow/services/ArtifactEnrichmentService";

export interface RunListStage {
  sequence_number: number | null;
  report_type: string | null;
  dialogue_id: number;
  status: "pending" | "in_progress" | "completed" | "failed";
  report_sdr_id: number | null;
}

export interface RunListItem {
  run_id: number;
  create_date: string | null;
  created_by: string | null;
  review_dialogue_id: number | null;
  template_name: string | null;
  stage_report_prefix: string | null;
  stage_report_suffix_label: string | null;
  stages: RunListStage[];
}

const getProposalGenerationRuns = async (
  limit: number,
): Promise<RunListItem[]> => {
  const response = await axios.get(
    `${nuclearn_api_url}/products/proposal-generation/runs`,
    { params: { limit } },
  );
  return response.data;
};

/**
 * Status-bearing run list for the runs dashboard (recents lists dialogues only).
 *
 * Refetches when the tab regains focus: a run advances on the agent's clock,
 * not the viewer's, so the common stale case is coming back to a tab left open
 * while a stage ran. Deliberately not polled — a dashboard that re-queries on
 * a timer costs every open tab a request per interval for state that changes
 * on the order of minutes, and Refresh covers the same-tab case.
 */
export const useGetProposalGenerationRuns = (limit: number = 50) => {
  return useQuery(
    ["proposalGenerationRuns", limit],
    () => getProposalGenerationRuns(limit),
    { refetchOnWindowFocus: true, staleTime: 30 * 1000 },
  );
};

/**
 * Create a new proposal generation run
 * This creates a run with all the necessary artifacts and dialogue association
 */
const createProposalGenerationRun = async (
  payload: CreateProposalGenerationRunPayload,
): Promise<number> => {
  const response = await axios.post(
    `${nuclearn_api_url}/products/proposal-generation/create-run`,
    payload,
  );
  return response.data;
};

export const useCreateProposalGenerationRun = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (payload: CreateProposalGenerationRunPayload) =>
      createProposalGenerationRun(payload),
    {
      onSuccess: () => {
        // Invalidate runs list queries
        queryClient.invalidateQueries(["proposalGenerationRun"]);
      },
      onError: (error: any) => {
        console.error("Failed to create proposal generation run:", error);
      },
    },
  );
};

/**
 * Add a dialogue to an existing proposal generation run
 */
const addDialogueToProposalGenerationRun = async (
  runId: number,
  payload: CreateProposalGenerationRunPayload,
): Promise<number> => {
  const response = await axios.post(
    `${nuclearn_api_url}/products/proposal-generation/run/${runId}/dialogues`,
    payload,
  );
  return response.data;
};

export const useAddDialogueToProposalGenerationRun = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      runId,
      payload,
    }: {
      runId: number;
      payload: CreateProposalGenerationRunPayload;
    }) => addDialogueToProposalGenerationRun(runId, payload),
    {
      onSuccess: (_data, variables) => {
        // Invalidate relevant queries for both the run and dialogue
        queryClient.invalidateQueries(["proposalGenerationRun"]);
        queryClient.invalidateQueries([
          "proposalGenerationRun",
          variables.payload.dialogue_id,
        ]);
      },
      onError: (error: any) => {
        console.error(
          "Failed to add dialogue to proposal generation run:",
          error,
        );
      },
    },
  );
};

/**
 * Get proposal generation data for a dialogue
 */
export const getProposalGenerationDialogueData = async (
  dialogueId: number,
): Promise<ProposalGenerationDialogueData> => {
  const response = await axios.get(
    `${nuclearn_api_url}/products/proposal-generation/dialogue/${dialogueId}/data`,
  );
  return response.data;
};

export const useGetProposalGenerationDialogueData = (
  dialogueId: number | undefined,
) => {
  return useQuery(
    ["proposalGenerationRun", dialogueId],
    () => getProposalGenerationDialogueData(dialogueId!),
    {
      enabled: !!dialogueId,
      refetchOnWindowFocus: false,
    },
  );
};

/**
 * A plan created by the agent. Mirrors the backend `PlanResponse` schema.
 * Refetched whenever a plan tool completes.
 */
export interface ProposalGenerationPlan {
  plan_id: string;
  name: string;
  content: string;
  status: "created" | "in_progress" | "completed" | "failed";
  result_summary: string | null;
  created_at: string | null;
  completed_at: string | null;
}

export const getProposalGenerationPlans = async (
  planIds: string[],
): Promise<ProposalGenerationPlan[]> => {
  const response = await axios.get(
    `${nuclearn_api_url}/products/proposal-generation/plans`,
    {
      params: { plan_ids: planIds },
      // FastAPI expects repeated `plan_ids=a&plan_ids=b` (no brackets);
      // axios's default qs serializer uses `plan_ids[]=...` which FastAPI ignores.
      paramsSerializer: {
        indexes: null,
      },
    },
  );
  return response.data;
};

/**
 * Fetch plans by their external plan_ids.
 * Callers should pass a deduped, sorted list so the cache key is stable.
 */
export const useGetProposalGenerationPlans = (planIds: string[]) => {
  return useQuery(
    ["proposalGenerationPlans", planIds],
    () => getProposalGenerationPlans(planIds),
    {
      enabled: planIds.length > 0,
      refetchOnWindowFocus: false,
    },
  );
};

/**
 * Update proposal generation data by dialogue
 */
const updateProposalGenerationDialogueData = async (
  dialogueId: number,
  payload: UpdateProposalGenerationDialogueData,
): Promise<ProposalGenerationDialogueData> => {
  const response = await axios.put(
    `${nuclearn_api_url}/products/proposal-generation/dialogue/${dialogueId}/data`,
    payload,
  );
  return response.data;
};

export const useUpdateProposalGenerationDialogueData = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      dialogueId,
      payload,
    }: {
      dialogueId: number;
      payload: UpdateProposalGenerationDialogueData;
    }) => updateProposalGenerationDialogueData(dialogueId, payload),
    {
      onSuccess: (_data, variables) => {
        // Invalidate relevant queries
        queryClient.invalidateQueries([
          "proposalGenerationRun",
          variables.dialogueId,
        ]);
      },
      onError: (error: any) => {
        console.error("Failed to update proposal generation data:", error);
      },
    },
  );
};

/**
 * Get all stages for a proposal generation run
 */
export const getProposalGenerationStages = async (
  runId: number,
): Promise<StageResponse[]> => {
  const response = await axios.get(
    `${nuclearn_api_url}/products/proposal-generation/run/${runId}/stages`,
  );
  return response.data;
};

export const useGetProposalGenerationStages = (runId: number | undefined) => {
  const queryClient = useQueryClient();
  const queryKey = ["proposalGenerationStages", runId];

  const query = useQuery(queryKey, () => getProposalGenerationStages(runId!), {
    enabled: !!runId,
    refetchInterval: (data) => {
      // SSE pushes stage changes (see notify:proposal_gen_run:{id}:stages).
      // Only fall back to polling when the stream is unhealthy; then keep the
      // original 3s cadence while any stage is still active.
      if (isNotificationStreamHealthy()) return false;
      const hasActiveStages = data?.some(
        (s) => s.status === "in_progress" || s.status === "pending",
      );
      return hasActiveStages ? 3000 : false;
    },
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
  });

  // Any event on the run's stage stream is a "refetch now" signal — status is
  // computed server-side, so we re-read the authoritative list rather than
  // patching the cache from the thin event payload.
  useNotificationStream(
    runId ? `notify:proposal_gen_run:${runId}:stages` : null,
    "proposal_gen_stages",
    () => queryClient.invalidateQueries(queryKey),
  );

  // Resume polling immediately if SSE drops (refetchInterval alone won't
  // re-run once it has returned false during a healthy period).
  useSSEFallbackInvalidation(queryClient, queryKey, !!runId);

  return query;
};

/**
 * Create generated child-report stages after user approval
 */
const createStages = async (
  runId: number,
  payload: CreateStagesPayload,
): Promise<number[]> => {
  const response = await axios.post(
    `${nuclearn_api_url}/products/proposal-generation/run/${runId}/create-stages`,
    payload,
  );
  return response.data;
};

export const useCreateStages = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ runId, payload }: { runId: number; payload: CreateStagesPayload }) =>
      createStages(runId, payload),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([
          "proposalGenerationStages",
          variables.runId,
        ]);
      },
      onError: (error: any) => {
        console.error("Failed to create stages:", error);
      },
    },
  );
};

/**
 * Identify child-report stages from the review report using the configured LLM prompt
 */
export interface IdentifiedStagesResponse {
  stages: string[];
  review_report_sdr_id: number;
}

const identifyStages = async (
  runId: number,
): Promise<IdentifiedStagesResponse> => {
  const response = await axios.post(
    `${nuclearn_api_url}/products/proposal-generation/run/${runId}/identify-stages`,
    null,
    {
      params: {},
    },
  );
  return response.data;
};

export const useIdentifyStages = () => {
  return useMutation(({ runId }: { runId: number }) => identifyStages(runId), {
    onError: (error: any) => {
      console.error("Failed to identify stages:", error);
    },
  });
};

/**
 * Clear a proposal generation stage, resetting it back to pending
 */
const clearProposalGenerationStage = async (
  dialogueId: number,
): Promise<void> => {
  await axios.post(
    `${nuclearn_api_url}/products/proposal-generation/dialogue/${dialogueId}/clear`,
  );
};

export const useClearProposalGenerationStage = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ dialogueId }: { dialogueId: number; runId: number }) =>
      clearProposalGenerationStage(dialogueId),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([
          "proposalGenerationStages",
          variables.runId,
        ]);
        queryClient.invalidateQueries([
          "proposalGenerationRun",
          variables.dialogueId,
        ]);
        // Invalidate the dialogue query so chat page re-fetches cleared messages
        queryClient.invalidateQueries(["dialogue", variables.dialogueId]);
      },
      onError: (error: any) => {
        console.error("Failed to clear proposal generation stage:", error);
      },
    },
  );
};

/**
 * Remove a stage (dialogue) from a proposal generation run
 */
const removeStageFromRun = async (
  runId: number,
  dialogueId: number,
): Promise<void> => {
  await axios.delete(
    `${nuclearn_api_url}/products/proposal-generation/run/${runId}/dialogue/${dialogueId}`,
  );
};

export const useRemoveStageFromRun = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ runId, dialogueId }: { runId: number; dialogueId: number }) =>
      removeStageFromRun(runId, dialogueId),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([
          "proposalGenerationStages",
          variables.runId,
        ]);
      },
      onError: (error: any) => {
        console.error("Failed to remove stage from run:", error);
      },
    },
  );
};

/**
 * Add a new stage to a proposal generation run
 */
export interface AddStagePayload {
  report_type: string;
  inherit_artifacts?: boolean;
}

export interface AddStageResponse {
  dialogue_id: number;
  sequence_number: number;
}

const addStageToRun = async (
  runId: number,
  payload: AddStagePayload,
): Promise<AddStageResponse> => {
  const response = await axios.post(
    `${nuclearn_api_url}/products/proposal-generation/run/${runId}/add-stage`,
    payload,
  );
  return response.data;
};

export const useAddStageToRun = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ runId, payload }: { runId: number; payload: AddStagePayload }) =>
      addStageToRun(runId, payload),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([
          "proposalGenerationStages",
          variables.runId,
        ]);
      },
      onError: (error: any) => {
        console.error("Failed to add stage to run:", error);
      },
    },
  );
};

// Legacy prefix is always recognised so pre-existing runs keep displaying
// correctly even after an admin reconfigures the stage prefix.
const LEGACY_STAGE_REPORT_PREFIX = "discipline_report_";

const titleCase = (value: string): string =>
  value
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

/**
 * Convert report_type to a human-readable display name.
 *
 * The generated-stage prefix and the display suffix are configurable; the
 * legacy "discipline_report_" prefix is always also recognised.
 * Examples (default config):
 * - "review report" -> "Review Report"
 * - "discipline_report_structural_engineering" -> "Structural Engineering Discipline Report"
 */
export const getReportTypeDisplayName = (
  reportType?: string,
  sequenceNumber?: number,
  config?: { report_type_prefix?: string; stage_report_suffix_label?: string },
): string => {
  if (!reportType) {
    return sequenceNumber === 1 ? "Review Report" : `Stage ${sequenceNumber}`;
  }

  // Handle review report
  if (reportType === "review report" || reportType === "review_report") {
    return "Review Report";
  }

  const suffixLabel = config?.stage_report_suffix_label || "Discipline Report";
  const configuredPrefix = config?.report_type_prefix;
  const matchedPrefix =
    configuredPrefix && reportType.startsWith(configuredPrefix)
      ? configuredPrefix
      : reportType.startsWith(LEGACY_STAGE_REPORT_PREFIX)
        ? LEGACY_STAGE_REPORT_PREFIX
        : null;

  if (matchedPrefix) {
    const stageName = titleCase(reportType.slice(matchedPrefix.length));
    return `${stageName} ${suffixLabel}`;
  }

  // Fallback - just title case the report type
  return titleCase(reportType);
};

/**
 * Build the display-config for getReportTypeDisplayName, preferring the run's
 * snapshotted prefix + suffix over the live config so older runs display
 * correctly after a config change. Each falls back to the live config when the
 * run has no snapshot yet.
 */
export const stageDisplayConfig = (
  runPrefix: string | null | undefined,
  runSuffix: string | null | undefined,
  effective?: {
    report_type_prefix?: string;
    stage_report_suffix_label?: string;
  },
): { report_type_prefix?: string; stage_report_suffix_label?: string } => ({
  report_type_prefix: runPrefix ?? effective?.report_type_prefix,
  stage_report_suffix_label: runSuffix ?? effective?.stage_report_suffix_label,
});
